/**
 * ActiveCallBanner
 *
 * Shows a green banner inside the chat area when a group call is active
 * on the current channel and the user is NOT already in it.
 * Provides a "Join Call" button for late-joining.
 *
 * Listens for `channel.updated` events so the banner disappears immediately
 * when `activeCallId` is unset (e.g. after endCallForAll or session cleanup).
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { Channel as StreamChannel } from "stream-chat";
import { useActiveCall } from "@/features/call/context/ActiveCallContext";

interface ActiveCallBannerProps {
  channel: StreamChannel;
  onJoin: (callId: string) => void;
}

export const ActiveCallBanner = ({ channel, onJoin }: ActiveCallBannerProps) => {
  const { t } = useTranslation();
  const { call } = useActiveCall();

  // Track activeCallId in state so we re-render when channel data changes.
  // Reading channel.data directly doesn't trigger re-renders because the SDK
  // mutates the object in place without changing the reference.
  const [activeCallId, setActiveCallId] = useState<string | undefined>(
    (channel.data as any)?.activeCallId,
  );

  useEffect(() => {
    let cancelled = false;

    // Sync immediately from cached data (fast path)
    setActiveCallId((channel.data as any)?.activeCallId);

    // Force a server-side query so users who navigate to this channel
    // AFTER the call started (and thus missed the channel.updated event)
    // still see the banner with the latest activeCallId.
    channel.query({}).then(() => {
      if (!cancelled) {
        setActiveCallId((channel.data as any)?.activeCallId);
      }
    }).catch(console.error);

    // Listen for channel.updated events to detect activeCallId changes
    const subscription = channel.on("channel.updated", (event) => {
      const updatedCallId = (event.channel as any)?.activeCallId as
        | string
        | undefined;
      setActiveCallId(updatedCallId);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [channel]);

  // Don't show if no active call on this channel, or user is already in this call
  if (!activeCallId || call?.id === activeCallId) return null;

  return (
    <div className="bg-green-50 border-b border-green-200 px-4 py-2.5 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        <span className="text-green-700 text-sm font-medium">
          {t("call.group_call_in_progress")}
        </span>
      </div>
      <button
        onClick={() => onJoin(activeCallId)}
        className="bg-green-600 text-white px-4 py-1.5 rounded-full text-sm font-medium hover:bg-green-700 transition-colors"
      >
        {t("call.join_call")}
      </button>
    </div>
  );
};
